import { useEffect, useRef, useState } from 'react';

// Custom React hook to manage a sticky header behavior using IntersectionObserver and ResizeObserver.
// Tracks when the header should stick (based on a sentinel element) and dynamically updates header height on resize.

export const useStickyHeader = () => {
    const headerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const [isSticky, setIsSticky] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const observerTarget = sentinelRef.current;
        if (!observerTarget) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsSticky(!entry.isIntersecting);
            },
            { threshold: 1.0 }
        );

        observer.observe(observerTarget);

        return () => {
            if (observerTarget) {
                observer.unobserve(observerTarget);
            }
        };
    }, []);

    useEffect(() => {
        const headerNode = headerRef.current;
        if (!headerNode) return;

        const updateHeight = () => setHeaderHeight(headerNode.offsetHeight);

        // Initial set
        updateHeight();

        // ResizeObserver - dynamic height
        const resizeObserver = new (window as any).ResizeObserver(updateHeight);
        resizeObserver.observe(headerNode);

        // Update on window resize
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, [headerRef.current]);

    return { headerRef, sentinelRef, isSticky, headerHeight };
};
